import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Flex, Text, Badge, Link, Button, ButtonGroup, Tag, Wrap, WrapItem } from '@chakra-ui/react';
import { cqAPI, HOST } from '../../lib/Auth';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Import FontAwesomeIcon component
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from "../../AppContext";

function CallDetail() {
  const { prevLocation } = useAppContext()
  const { callId } = useParams();
  const [callDetails, setCallDetails] = useState({
    callObj: {}
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate()
  const [audioSrc, setAudioSrc] = useState('')
  // SS-4279: transcript translation
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState('');
  const [showEnglish, setShowEnglish] = useState(false);
  // the call the loaded details belong to: they lag the route until its details arrive
  const [detailsCallId, setDetailsCallId] = useState(null);
  // bumped on every navigation: a translation answered after the page moved on (even back to
  // the same call) is dropped
  const navigation = useRef(0);
  let file_url;

  const fetchCallDetails = async () => {
    const requestedFor = callId;
    const loadedAt = navigation.current;
    try {
      const response = await cqAPI.get(`/reports/call?id=${requestedFor}`);
      if (navigation.current !== loadedAt) {
        // the page moved on: these are not the details of the call it shows now
        return;
      }
      setCallDetails(response.data);
      setDetailsCallId(requestedFor);
      const recordings = response.data?.callObj?.recordings ?? [];
      const full_recording = recordings.find(rec => rec.type === "full") ?? recordings.find(rec => rec.type === "unknown") ?? null;
      if (full_recording) {
        // show the full recording if it exists in recordings
        file_url = full_recording.file_obj;
      }
      else if (response.data?.callObj?.recording_file_obj){
        file_url = response.data.callObj.recording_file_obj;
      }
      if (file_url) {
        setAudioSrc(await cqAPI.downloadFile(file_url).catch(e => null));
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching call details:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    navigation.current += 1;
    setDetailsCallId(null);
    setTranslating(false);
    setTranslateError('');
    setShowEnglish(false);
    fetchCallDetails();
  }, [callId]);

  // after a 409: re-read the transcript (and what can be done with it) as the server has it
  // now. The recording is not downloaded again, and a failed read leaves the page as it is —
  // the next Translate gets the same 409 and reloads again.
  const reloadTranscript = async () => {
    const loadedAt = navigation.current;
    const response = await cqAPI.get(`/reports/call?id=${callId}`).catch(() => null);
    if (navigation.current !== loadedAt || response?.type !== 'ok' || !response?.data) {
      return;
    }
    setCallDetails(response.data);
  };

  const translateTranscript = async () => {
    const pressedAt = navigation.current;
    setTranslating(true);
    setTranslateError('');
    try {
      // the transcript this page shows: the server refuses (409 transcript_changed) if it has moved on
      const sourceHash = callDetails?.call_translation?.source_hash;
      // one attempt: a failed translation is retried by the person, not the transport
      const response = await cqAPI.post('/reports/call/translate', {
        id: callId,
        ...(sourceHash ? { source_hash: sourceHash } : {}),
      }, undefined, { attempts: 1 });
      if (navigation.current !== pressedAt) {
        return;
      }
      // the office declined the credit confirmation (cqAPI resolves false when the
      // NEEDS_CONFIRMATION dialog is cancelled): nothing was translated or charged
      if (response === false) {
        return;
      }
      if (response?.type === 'ok' && response?.data?.translated?.dialog?.length) {
        const translated = response.data.translated;
        setCallDetails(prev => ({ ...prev, call_translation: { ...prev.call_translation, translated } }));
        setShowEnglish(true);
      } else if (['transcript_not_ready', 'transcript_changed'].includes(response?.reason)) {
        setTranslateError('Transcript is still being finalized, try again shortly.');
        // the transcript on screen is not the server's any more: reload it (Translate stays
        // busy until it arrives), so a retry translates the transcript the page shows
        await reloadTranscript();
      } else {
        setTranslateError(response?.reason && response?.msg ? response.msg : 'The translation could not be completed. Please try again.');
      }
    } catch (error) {
      console.error('Error translating the transcript:', error);
      if (navigation.current === pressedAt) {
        setTranslateError('The translation could not be completed. Please try again.');
      }
    } finally {
      if (navigation.current === pressedAt) {
        setTranslating(false);
      }
    }
  };

  if (loading) {
    return <Box>Loading...</Box>;
  }

  const formatDate = (date) => {
    return moment(date).format('MMM D, YYYY h:mm:ss A');
  };
  
  const handleBackClick = () => {
      navigate(-1); 
    };
    
    const downloadFile = async (attachmentUrl, fileName) => {
      try {

        // Create a temporary link element and trigger the download
        const link = document.createElement('a');
        link.href = audioSrc
        link.setAttribute('download', fileName); // Set the file name for the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Failed to download file:', error);
      }
    };

  // --- Conversation topics (prefer callObj.metadata, fallback to top-level metadata, then callObj.conversation_topics) ---
  const topics = (() => {
    const fromCallObjMeta = (callDetails?.callObj?.metadata || [])
      .flatMap(m => m?.conversation_topics || [])
      .filter(Boolean);
    if (fromCallObjMeta.length) return Array.from(new Set(fromCallObjMeta));

    const fromTopLevelMeta = (callDetails?.metadata || [])
      .flatMap(m => m?.conversation_topics || [])
      .filter(Boolean);
    if (fromTopLevelMeta.length) return Array.from(new Set(fromTopLevelMeta));

    return callDetails?.callObj?.conversation_topics || [];
  })();

  // Translation is offered only on the turn-by-turn transcript, once the API says it is final
  const callTranslation = callDetails?.call_translation;
  const hasTimeline = !!(callDetails?.timelineObj && callDetails.timelineObj.length);
  const translatedDialog = hasTimeline && callTranslation?.translated?.dialog?.length ? callTranslation.translated.dialog : null;
  const showTranslateButton = detailsCallId === callId && hasTimeline && !!callTranslation
    && callTranslation.transcript_ready === true && !translatedDialog;
  const shownDialog = translatedDialog && showEnglish ? translatedDialog : callDetails?.timelineObj;

  return (
    <Flex direction="column" p="5" overflowY="auto" maxH="100vh"> {/* Enable scrolling */}
      <Box className="call-details" w="100%" textAlign="left">
    <Box textAlign="left" mb="5">
        {prevLocation === '/reports/calls' && (
          <Button onClick={handleBackClick} variant="ghost">
            <FontAwesomeIcon icon={faArrowLeft} /> 
            Back to call list
          </Button>
        )}
      </Box>
        {!callDetails?.callObj && "Could not find a recording for this call"}
        { callDetails?.callObj &&
        <Flex wrap="wrap">
          <Box p="2" w="200px">
            <Text fontWeight="bold">Phone #</Text>
            <Text >{callDetails.callObj?.type === 'Incoming' ? callDetails?.callObj?.from : callDetails?.callObj?.to}</Text>
            {callDetails?.callObj?.name ? (
              <Text>{callDetails?.callObj?.name}</Text>
            ) : callDetails?.patient_name ? (
              <Text>{callDetails?.patient_name}</Text>
            ) : ""}
          </Box>

          <Box p="2" w="200px">
            <Text fontWeight="bold">Date & Time</Text>
            <Text>{formatDate(callDetails?.callObj?.created)}</Text>
            {callDetails?.callObj?.hangup && callDetails.callObj.hangup.patient != null && (
              <Text fontSize="sm" color="gray.500" mt="1">
                Ended by {callDetails.callObj.hangup.patient ? 'Patient' : 'Office'}
                {callDetails.callObj.hangup.date && ` at ${moment(callDetails.callObj.hangup.date).format('h:mm:ss A')}`}
              </Text>
            )}
          </Box>

          <Box p="2" w="200px">
            <Text fontWeight="bold">Extension</Text>
            <Text>{callDetails?.callObj?.extension && callDetails?.callObj?.extension?.numeric_value}</Text>
          </Box>

          <Box p="2" w="200px">
            <Text fontWeight="bold">Duration</Text>
            <Text>{callDetails?.callObj?.duration}</Text>
          </Box>

          {file_url !== null && (
            <Box p="2" w="300px">
              <Text fontWeight="bold">Recording</Text>
            <Link
              onClick={(e) => {
                e.preventDefault();
                downloadFile(file_url, `${(callDetails?.callObj?.name || '').toLowerCase().replace(/\ /g,'-') || callDetails?.callObj}.wav`);
            }}
            cursor="pointer"
          >
            Download
          </Link>
              <audio controls style={{ width: '100%', marginTop: '10px' }}>
                <source src={audioSrc} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </Box>
          )}

          {callDetails?.transcription && callDetails?.transcription?.indicators && (
            <Box p="2" w="200px">
              <Text fontWeight="bold">Indicators</Text>
              {callDetails.transcription?.indicators.map((indicator, index) => (
                <Badge key={index} colorScheme="green" m="1">
                  {indicator}
                </Badge>
              ))}
            </Box>
          )}

          {topics && topics.length > 0 && (
            <Box p="2" w="200px">
              <Text fontWeight="bold">Conversation Topics</Text>
              <Wrap spacing="2" mt="1">
                {topics.map((t, i) => (
                  <WrapItem key={`${t}-${i}`}>
                    <Tag size="sm" variant="subtle">
                      {t}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            </Box>
          )}
        </Flex>
        }
        <Box mt="4">
          <Flex align="center" mb="2" gap="3" wrap="wrap">
            <Text fontSize="2xl">Transcription</Text>
            {showTranslateButton && (
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={translateTranscript}
                isLoading={translating}
                loadingText="Translating..."
                isDisabled={callTranslation.is_english === true}
                title={callTranslation.is_english === true ? 'This call is already in English' : undefined}
              >
                Translate
              </Button>
            )}
            {translatedDialog && (
              <ButtonGroup size="sm" isAttached variant="outline">
                <Button onClick={() => setShowEnglish(false)} isActive={!showEnglish} aria-pressed={!showEnglish}>
                  Original
                </Button>
                <Button onClick={() => setShowEnglish(true)} isActive={showEnglish} aria-pressed={showEnglish}>
                  English
                </Button>
              </ButtonGroup>
            )}
          </Flex>
          {translateError && (
            <Text color="red.500" fontSize="sm" mb="2" role="alert">{translateError}</Text>
          )}
          {hasTimeline ? (
            shownDialog.map((item, index) => (
              item && (
                <Box key={index} p="2">
                  <Text color={item.chan === 'Agent' ? 'blue.500' : 'green.500'}>
                    <strong>{item.chan}: </strong>{item.content}
                  </Text>
                </Box>
              )
            ))
          ) : (callDetails?.transcription?.raw_data?.results?.transcripts?.length) ? (
                <Box p="2" w="1000px">
                  <Text color='green.500' m="1">
                    {callDetails.transcription.raw_data.results.transcripts[0].transcript}
                  </Text>
                </Box>
            ) : (
            <Text>Not available</Text>
          )}
        </Box>
      </Box>
    </Flex>
  );
}

export default CallDetail;
